import React, { useState, useEffect, useRef } from "react";
import {
  Box, Typography, Button, TextField, Select, MenuItem,
  FormControl, InputLabel, LinearProgress, CircularProgress,
  Paper, Chip,
} from "@mui/material";
import axios from "axios";
import { useNavigate } from "react-router-dom";

const API = process.env.REACT_APP_API_URL || "";

const D = {
  bg:         "#0a0a0f",
  surface:    "#111118",
  surfaceHov: "#16161f",
  border:     "#1e1e2e",
  borderMed:  "#2a2a3e",
  accent:     "#7c6aff",
  accentSoft: "rgba(124,106,255,0.12)",
  accentBord: "rgba(124,106,255,0.25)",
  text:       "#e8e8f0",
  textMuted:  "#6b6b8a",
  textSec:    "#9898b0",
  success:    "#22c55e",
  danger:     "#ef4444",
  dangerSoft: "rgba(239,68,68,0.1)",
  font:       "'Inter', sans-serif",
  mono:       "'JetBrains Mono', monospace",
};

const CATEGORIES = [
  { key: "welcome_series",   label: "Welcome Series",              emoji: "👋", desc: "Onboard new subscribers and set expectations for your relationship." },
  { key: "sales_nurture",    label: "Sales Nurture",               emoji: "🎯", desc: "Move warm leads from interest to purchase with trust-building content." },
  { key: "abandoned_cart",   label: "Abandoned Cart Recovery",     emoji: "🛒", desc: "Re-engage people who showed interest but didn't complete purchase." },
  { key: "post_purchase",    label: "Post-Purchase Onboarding",    emoji: "✅", desc: "Get new customers activated, delivering results, and ready to refer." },
  { key: "reengagement",     label: "Re-Engagement / Win-Back",    emoji: "🔄", desc: "Bring back subscribers or customers who have gone quiet." },
  { key: "product_launch",   label: "Product Launch",              emoji: "🚀", desc: "Build anticipation, overcome objections, and drive conversions at launch." },
  { key: "lead_magnet",      label: "Lead Magnet Follow-Up",       emoji: "🧲", desc: "Nurture leads who downloaded a resource into paying customers." },
  { key: "event_webinar",    label: "Event / Webinar Sequence",    emoji: "🎙️", desc: "Drive registrations, increase show rate, and follow up for conversions." },
  { key: "promotional",      label: "Promotional / Flash Sale",    emoji: "⚡", desc: "Time-sensitive campaign to drive urgency and maximize conversion." },
  { key: "educational_drip", label: "Educational Drip",            emoji: "📚", desc: "Build authority and trust by teaching subscribers over several weeks." },
];

const TONES = [
  { value: "conversational", label: "Conversational", desc: "Warm, personal — like a trusted friend who happens to be an expert" },
  { value: "professional",   label: "Professional",   desc: "Polished and authoritative — confident without being cold" },
  { value: "urgent",         label: "Urgent",         desc: "Direct and action-oriented — every word earns its place" },
  { value: "educational",    label: "Educational",    desc: "Clear and instructive — teach first, sell second" },
];

const TRIGGER_OPTIONS = [
  "New subscriber",
  "Lead magnet download",
  "Ad click / landing page",
  "Existing customer",
  "Booked a call",
  "Event registration",
  "Product trial started",
];

const CTA_OPTIONS = [
  "Book a call",
  "Buy the product",
  "Attend the event",
  "Download a resource",
  "Complete onboarding",
  "Upgrade to paid",
  "Refer a friend",
];

export default function EmailSequenceWizard() {
  const navigate = useNavigate();
  const [hasAccess, setHasAccess]   = useState(null);
  const [credits,   setCredits]     = useState(null);

  // Form state
  const [category,    setCategory]   = useState("welcome_series");
  const [topic,       setTopic]      = useState("");
  const [tone,        setTone]       = useState("conversational");
  const [numEmails,   setNumEmails]  = useState(7);
  const [trigger,     setTrigger]    = useState("New subscriber");
  const [primaryCta,  setPrimaryCta] = useState("Book a call");
  const [audience,    setAudience]   = useState("");
  const [extraCtx,    setExtraCtx]   = useState("");

  // Job state
  const [jobId,      setJobId]      = useState(null);
  const [status,     setStatus]     = useState(null);
  const [pct,        setPct]        = useState(0);
  const [jobMeta,    setJobMeta]    = useState(null);
  const [generating, setGenerating] = useState(false);
  const [error,      setError]      = useState("");
  const pollRef = useRef(null);

  // Auth + credits check
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) { navigate("/login"); return; }
    axios.get(`${API}/api/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => {
        if (!r.data?.has_email_sequences) { navigate("/home", { replace: true }); return; }
        setHasAccess(true);
        // fetch credits
        return axios.get(`${API}/api/email-sequences/credits`, { headers: { Authorization: `Bearer ${token}` } });
      })
      .then(r => { if (r) setCredits(r.data?.credits ?? 0); })
      .catch(() => navigate("/login"));
  }, [navigate]);

  // Poll for status
  useEffect(() => {
    if (!jobId || status === "complete" || status === "failed") return;
    pollRef.current = setInterval(async () => {
      try {
        const token = localStorage.getItem("token");
        const r = await axios.get(`${API}/api/email-sequences/status/${jobId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setStatus(r.data.status);
        setPct(r.data.pct || 0);
        if (r.data.status === "complete" || r.data.status === "failed") {
          setJobMeta(r.data);
          clearInterval(pollRef.current);
          setGenerating(false);
          if (r.data.status === "complete") setCredits(c => Math.max(0, (c ?? 1) - 1));
        }
      } catch { /* non-fatal */ }
    }, 6000);
    return () => clearInterval(pollRef.current);
  }, [jobId, status]);

  const handleGenerate = async () => {
    if (!topic.trim() || topic.trim().length < 10) {
      setError("Topic / context must be at least 10 characters.");
      return;
    }
    setError("");
    setGenerating(true);
    setStatus("queued");
    setPct(5);
    setJobMeta(null);
    try {
      const token = localStorage.getItem("token");
      const r = await axios.post(`${API}/api/email-sequences/generate`, {
        topic:              topic.trim(),
        category,
        tone,
        num_emails:         numEmails,
        trigger:            trigger,
        primary_cta:        primaryCta,
        audience:           audience.trim(),
        additional_context: extraCtx.trim(),
      }, { headers: { Authorization: `Bearer ${token}` } });
      setJobId(r.data.job_id);
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to start generation.");
      setGenerating(false);
      setStatus(null);
    }
  };

  const handleDownload = async () => {
    try {
      const token = localStorage.getItem("token");
      const r = await axios.get(`${API}/api/email-sequences/download/${jobId}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });
      const url  = URL.createObjectURL(new Blob([r.data], { type: "application/zip" }));
      const link = document.createElement("a");
      link.href     = url;
      link.download = jobMeta?.filename || "email_sequence.zip";
      link.click();
      URL.revokeObjectURL(url);
    } catch { setError("Download failed. Please try again."); }
  };

  const handleReset = () => {
    setJobId(null); setStatus(null); setPct(0);
    setJobMeta(null); setGenerating(false); setError("");
  };

  const inputSx = {
    "& .MuiOutlinedInput-root": {
      bgcolor: D.surface, color: D.text, fontFamily: D.font, fontSize: "14px",
      borderRadius: "10px",
      "& fieldset": { borderColor: D.border },
      "&:hover fieldset": { borderColor: D.borderMed },
      "&.Mui-focused fieldset": { borderColor: D.accent },
    },
    "& .MuiInputLabel-root": { color: D.textMuted, fontFamily: D.font, fontSize: "13px" },
    "& .MuiInputLabel-root.Mui-focused": { color: D.accent },
    "& .MuiFormHelperText-root": { fontFamily: D.font },
  };

  const selectMenuSx = { PaperProps: { sx: { bgcolor: "#16161f", border: `1px solid ${D.border}`, borderRadius: "10px", mt: 0.5 } } };

  if (hasAccess === null) return (
    <Box sx={{ minHeight: "100vh", bgcolor: D.bg, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <CircularProgress sx={{ color: D.accent }} />
    </Box>
  );

  const selectedCat = CATEGORIES.find(c => c.key === category);

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: D.bg, py: 6, px: 2 }}>
      <Box sx={{ maxWidth: 680, mx: "auto" }}>

        {/* Header */}
        <Box sx={{ mb: 5 }}>
          <Button
            onClick={() => navigate("/")}
            sx={{ color: D.textMuted, fontFamily: D.font, textTransform: "none", fontSize: "13px", mb: 2, pl: 0, "&:hover": { color: D.text, bgcolor: "transparent" } }}
          >
            ← Back
          </Button>
          <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 2 }}>
            <Box>
              <Typography sx={{ fontSize: "26px", fontWeight: 700, color: D.text, fontFamily: D.font, letterSpacing: "-0.02em" }}>
                Email Sequence Wizard
              </Typography>
              <Typography sx={{ fontSize: "14px", color: D.textMuted, fontFamily: D.font, mt: 0.5 }}>
                Generate 3, 5, or 7 hand-crafted emails — subject lines, preview text, body, and CTA for every email.
              </Typography>
            </Box>
            {credits !== null && (
              <Box sx={{ flexShrink: 0, textAlign: "right" }}>
                <Typography sx={{ fontFamily: D.mono, fontSize: "22px", fontWeight: 700, color: credits > 0 ? D.accent : D.danger }}>
                  {credits}
                </Typography>
                <Typography sx={{ fontFamily: D.font, fontSize: "11px", color: D.textMuted }}>credits left</Typography>
              </Box>
            )}
          </Box>
        </Box>

        {/* Form */}
        {!jobId && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>

            {/* Category picker */}
            <Box>
              <Typography sx={{ fontSize: "12px", fontFamily: D.mono, color: D.textMuted, textTransform: "uppercase", letterSpacing: "0.07em", mb: 1.5 }}>
                Sequence Type
              </Typography>
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1 }}>
                {CATEGORIES.map(cat => (
                  <Box
                    key={cat.key}
                    onClick={() => setCategory(cat.key)}
                    sx={{
                      p: 1.5, borderRadius: "10px", cursor: "pointer", transition: "all 0.15s",
                      border: `1px solid ${category === cat.key ? D.accent : D.border}`,
                      bgcolor: category === cat.key ? D.accentSoft : D.surface,
                      "&:hover": { borderColor: category === cat.key ? D.accent : D.borderMed, bgcolor: category === cat.key ? D.accentSoft : D.surfaceHov },
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.4 }}>
                      <span style={{ fontSize: 15 }}>{cat.emoji}</span>
                      <Typography sx={{ fontFamily: D.font, fontWeight: 600, fontSize: "13px", color: category === cat.key ? D.accent : D.text }}>
                        {cat.label}
                      </Typography>
                    </Box>
                    <Typography sx={{ fontFamily: D.font, fontSize: "11.5px", color: D.textMuted, lineHeight: 1.4 }}>
                      {cat.desc}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>

            {/* Topic */}
            <Paper sx={{ bgcolor: D.surface, border: `1px solid ${D.border}`, borderRadius: "14px", p: 3, display: "flex", flexDirection: "column", gap: 2.5 }}>
              <TextField
                label="Topic / Product / Context"
                placeholder={`e.g. "${selectedCat?.label}" sequence for our AI content scheduling tool — targeting marketing managers at startups`}
                value={topic}
                onChange={e => setTopic(e.target.value)}
                fullWidth multiline minRows={2} sx={inputSx}
                helperText={
                  <Typography component="span" sx={{ fontSize: "11px", color: topic.length < 10 ? D.danger : D.textMuted, fontFamily: D.font }}>
                    {topic.length < 10 ? `${10 - topic.length} more characters needed` : "Good — the more specific, the better the emails"}
                  </Typography>
                }
              />

              {/* Sequence length */}
              <Box>
                <Typography sx={{ fontSize: "12px", fontFamily: D.mono, color: D.textMuted, textTransform: "uppercase", letterSpacing: "0.07em", mb: 1 }}>
                  Sequence Length
                </Typography>
                <Box sx={{ display: "flex", gap: 1 }}>
                  {[
                    { n: 3, desc: "Short · high intent" },
                    { n: 5, desc: "Balanced nurture" },
                    { n: 7, desc: "Full funnel" },
                  ].map(({ n, desc }) => (
                    <Box
                      key={n}
                      onClick={() => setNumEmails(n)}
                      sx={{
                        flex: 1, p: 1.5, borderRadius: "10px", cursor: "pointer", textAlign: "center",
                        border: `1.5px solid ${numEmails === n ? D.accent : D.border}`,
                        background: numEmails === n ? D.accentSoft : "transparent",
                        transition: "all 0.15s",
                        "&:hover": { borderColor: numEmails === n ? D.accent : D.borderMed },
                      }}
                    >
                      <Typography sx={{ fontSize: "18px", fontWeight: 700, color: numEmails === n ? D.accent : D.text, fontFamily: D.font }}>{n}</Typography>
                      <Typography sx={{ fontSize: "10px", color: D.textMuted, fontFamily: D.font }}>emails</Typography>
                      <Typography sx={{ fontSize: "10px", color: numEmails === n ? D.accent : D.textMuted, fontFamily: D.font, mt: 0.3 }}>{desc}</Typography>
                    </Box>
                  ))}
                </Box>
              </Box>

              <Box sx={{ display: "flex", gap: 2 }}>
                <FormControl fullWidth sx={inputSx}>
                  <InputLabel>Entry Trigger</InputLabel>
                  <Select value={trigger} onChange={e => setTrigger(e.target.value)} label="Entry Trigger"
                    sx={{ color: D.text, fontFamily: D.font, fontSize: "14px" }}
                    MenuProps={selectMenuSx}>
                    {TRIGGER_OPTIONS.map(t => (
                      <MenuItem key={t} value={t} sx={{ color: D.text, fontFamily: D.font, fontSize: "14px", "&:hover": { bgcolor: D.accentSoft } }}>
                        {t}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl fullWidth sx={inputSx}>
                  <InputLabel>Primary CTA</InputLabel>
                  <Select value={primaryCta} onChange={e => setPrimaryCta(e.target.value)} label="Primary CTA"
                    sx={{ color: D.text, fontFamily: D.font, fontSize: "14px" }}
                    MenuProps={selectMenuSx}>
                    {CTA_OPTIONS.map(c => (
                      <MenuItem key={c} value={c} sx={{ color: D.text, fontFamily: D.font, fontSize: "14px", "&:hover": { bgcolor: D.accentSoft } }}>
                        {c}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              <FormControl fullWidth sx={inputSx}>
                <InputLabel>Tone</InputLabel>
                <Select value={tone} onChange={e => setTone(e.target.value)} label="Tone"
                  sx={{ color: D.text, fontFamily: D.font, fontSize: "14px" }}
                  MenuProps={selectMenuSx}>
                  {TONES.map(t => (
                    <MenuItem key={t.value} value={t.value} sx={{ color: D.text, fontFamily: D.font, fontSize: "14px", "&:hover": { bgcolor: D.accentSoft } }}>
                      <Box>
                        <Typography sx={{ fontSize: "13px", fontWeight: 600, color: D.text, fontFamily: D.font }}>{t.label}</Typography>
                        <Typography sx={{ fontSize: "11px", color: D.textMuted, fontFamily: D.font }}>{t.desc}</Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                label="Target Audience (optional)"
                placeholder="e.g. E-commerce founders with $1M–$10M in annual revenue"
                value={audience}
                onChange={e => setAudience(e.target.value)}
                fullWidth sx={inputSx}
              />

              <TextField
                label="Additional Context (optional)"
                placeholder="e.g. We have a 14-day free trial. Main objection is implementation time. Competitor is Klaviyo."
                value={extraCtx}
                onChange={e => setExtraCtx(e.target.value)}
                fullWidth multiline minRows={2} sx={inputSx}
              />

              {error && (
                <Typography sx={{ fontSize: "13px", color: D.danger, fontFamily: D.font }}>{error}</Typography>
              )}

              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 1 }}>
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                  <Chip label={`${numEmails} emails`} size="small" sx={{ bgcolor: D.accentSoft, color: D.accent, fontFamily: D.mono, fontSize: "11px", border: `1px solid ${D.accentBord}` }} />
                  <Chip label="Subject + Preview + Body + CTA" size="small" sx={{ bgcolor: "rgba(255,255,255,0.04)", color: D.textSec, fontFamily: D.mono, fontSize: "11px", border: `1px solid ${D.border}` }} />
                  <Chip label="ZIP download" size="small" sx={{ bgcolor: "rgba(255,255,255,0.04)", color: D.textSec, fontFamily: D.mono, fontSize: "11px", border: `1px solid ${D.border}` }} />
                </Box>
                <Typography sx={{ fontFamily: D.mono, fontSize: "11px", color: D.textMuted }}>
                  1 credit
                </Typography>
              </Box>

              <Button
                onClick={handleGenerate}
                disabled={topic.trim().length < 10 || credits === 0}
                fullWidth
                sx={{
                  bgcolor: D.accent, color: "#fff", fontFamily: D.font, fontWeight: 600,
                  textTransform: "none", borderRadius: "10px", py: 1.5, fontSize: "15px",
                  "&:hover": { bgcolor: "#6a58e8" },
                  "&:disabled": { bgcolor: D.border, color: D.textMuted },
                }}
              >
                {credits === 0 ? "No credits remaining" : "Generate Email Sequence"}
              </Button>
            </Paper>
          </Box>
        )}

        {/* Generating */}
        {jobId && status !== "complete" && status !== "failed" && (
          <Paper sx={{ bgcolor: D.surface, border: `1px solid ${D.border}`, borderRadius: "14px", p: 4, textAlign: "center" }}>
            <Typography sx={{ fontSize: "16px", fontWeight: 600, color: D.text, fontFamily: D.font, mb: 1 }}>
              Writing your email sequence…
            </Typography>
            <Typography sx={{ fontSize: "13px", color: D.textMuted, fontFamily: D.font, mb: 3 }}>
              {numEmails} emails — subject lines, preview text, full body copy, and CTA for each. Usually under 60 seconds.
            </Typography>
            <LinearProgress
              variant="determinate" value={pct}
              sx={{ height: 6, borderRadius: 3, mb: 1, bgcolor: D.border, "& .MuiLinearProgress-bar": { bgcolor: D.accent, borderRadius: 3 } }}
            />
            <Typography sx={{ fontSize: "12px", color: D.textMuted, fontFamily: D.font }}>{pct}%</Typography>
          </Paper>
        )}

        {/* Complete */}
        {status === "complete" && jobMeta && (
          <Paper sx={{ bgcolor: D.surface, border: `1px solid ${D.border}`, borderRadius: "14px", p: 4 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 3 }}>
              <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: D.success }} />
              <Typography sx={{ fontSize: "16px", fontWeight: 600, color: D.text, fontFamily: D.font }}>
                Sequence ready — {jobMeta.email_count || 7} emails
              </Typography>
            </Box>

            {/* Subject line preview */}
            {jobMeta.subjects?.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography sx={{ fontFamily: D.mono, fontSize: "10.5px", fontWeight: 600, color: D.accent, letterSpacing: "0.07em", mb: 1.5, textTransform: "uppercase" }}>
                  Subject Lines
                </Typography>
                {jobMeta.subjects.map((subj, i) => (
                  <Box key={i} sx={{ display: "flex", gap: 1.5, mb: 0.75, alignItems: "baseline" }}>
                    <Typography sx={{ fontFamily: D.mono, fontSize: "10px", color: D.textMuted, minWidth: 24 }}>
                      #{String(i + 1).padStart(2, "0")}
                    </Typography>
                    <Typography sx={{ fontSize: "13px", color: "#c0c7de", fontFamily: D.font }}>
                      {subj}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}

            <Box sx={{ display: "flex", gap: 2 }}>
              <Button
                onClick={handleDownload} fullWidth
                sx={{
                  bgcolor: D.accent, color: "#fff", fontFamily: D.font, fontWeight: 600,
                  textTransform: "none", borderRadius: "10px", py: 1.4, fontSize: "14px",
                  "&:hover": { bgcolor: "#6a58e8" },
                }}
              >
                Download ZIP
              </Button>
              <Button
                onClick={handleReset}
                sx={{
                  color: D.textMuted, fontFamily: D.font, textTransform: "none",
                  border: `1px solid ${D.border}`, borderRadius: "10px", px: 3,
                  "&:hover": { borderColor: D.borderMed, color: D.text },
                }}
              >
                New Sequence
              </Button>
            </Box>
          </Paper>
        )}

        {/* Failed */}
        {status === "failed" && (
          <Paper sx={{ bgcolor: D.dangerSoft, border: `1px solid ${D.danger}`, borderRadius: "14px", p: 3, textAlign: "center" }}>
            <Typography sx={{ fontSize: "15px", fontWeight: 600, color: D.danger, fontFamily: D.font, mb: 1 }}>
              Generation failed
            </Typography>
            <Typography sx={{ fontSize: "13px", color: D.textMuted, fontFamily: D.font, mb: 2 }}>
              {jobMeta?.error || "An unexpected error occurred. Your credit has been refunded."}
            </Typography>
            <Button onClick={handleReset} sx={{ color: D.accent, fontFamily: D.font, textTransform: "none" }}>
              Try again
            </Button>
          </Paper>
        )}
      </Box>
    </Box>
  );
}
